import { createContext, useContext } from 'react';
import { linkOptions, useRouter } from '@tanstack/react-router';

import { useWorkflows } from '@/features/workflows';
import type { WorkflowView } from '@/lib/api/generated/model';

/**
 * The key a board address uses for a workflow, from the id a relation carries.
 * `undefined` when the workflow is no longer in the workspace, which is the one
 * case where a related task cannot be opened.
 */
export function resolveWorkflowKey(
  workflows: WorkflowView[],
  workflowId: string,
): string | undefined {
  return workflows.find((workflow) => workflow.id === workflowId)?.key;
}

/**
 * A related task's drawer on its workflow's board, open on its relations tab.
 * `null` without a workflow key: a board address cannot be built without one.
 */
export function boardHost(
  workspaceId: string,
  workflowKey: string | undefined,
  taskId: string,
) {
  if (!workflowKey) return null;
  return linkOptions({
    to: '/workspaces/$workspaceId/workflows/$workflowKey/board',
    params: { workspaceId, workflowKey },
    search: { task: taskId, tab: 'relations' as const },
  });
}

/**
 * Related tasks' drawers on the Tasks page (spec 111), keeping the list
 * narrowed as it is: `listSearch` is the page's current narrowing as address
 * parameters. Needs no workflow key — the Tasks page opens any task the
 * workspace holds, and the drawer says so itself if it cannot place one.
 */
export function tasksPageHost(
  // The page's own address parameters, carried as they are — this package
  // does not need to know what they mean, only to keep them.
  listSearch: Readonly<Record<string, string | undefined>>,
) {
  return (
    workspaceId: string,
    _workflowKey: string | undefined,
    taskId: string,
  ) =>
    linkOptions({
      to: '/workspaces/$workspaceId/tasks',
      params: { workspaceId },
      search: { ...listSearch, task: taskId, tab: 'relations' as const },
    });
}

/** Router link props for a related task's drawer, open on its relations tab. */
export type RelatedTaskLinkProps =
  | NonNullable<ReturnType<typeof boardHost>>
  | ReturnType<ReturnType<typeof tasksPageHost>>;

/** Where related-task links go from wherever the drawer is being shown. */
export type RelatedTaskHost = (
  workspaceId: string,
  workflowKey: string | undefined,
  taskId: string,
) => RelatedTaskLinkProps | null;

/**
 * Which page the task drawer is on, as far as its links are concerned.
 *
 * The drawer is shown on a board and on the Tasks page (spec 111), and a
 * related task should open *where the reader already is*. Every link to a
 * related task — the relations list, the wider-workflow groups, the diagram,
 * and the actions menu's own "Relations" item — goes through
 * `useRelatedTaskLink`, so the host decides once, here, instead of a prop being
 * threaded through the drawer, its panels and their rows.
 *
 * The board is the default, so nothing outside the Tasks page changes.
 */
const RelatedTaskHostContext = createContext<RelatedTaskHost>(boardHost);

export const RelatedTaskHostProvider = RelatedTaskHostContext.Provider;

/**
 * Where a related task lives, as an address rather than as a side effect.
 *
 * This replaced a hook that called `window.open(..., '_blank')`. That forced a
 * new browser tab on everyone whether they wanted one or not, never closed the
 * view it was opened from, and returned `false` with no message when the
 * workflow key could not be resolved — a control that silently did nothing.
 *
 * As props for a real link, the browser owns the gestures it already owns:
 * modifier-click and middle-click open a new tab because the person asked, the
 * address bar previews the destination, and "copy link address" works. The same
 * shape the Results tab uses for files (`filePreviewLink`).
 *
 * `tab: 'relations'` so a chain of connections can be walked one hop at a time
 * without re-selecting the tab at every step.
 *
 * Returns `null` when the workflow cannot be resolved, which the row renders as
 * a sentence rather than as a link that goes nowhere.
 */
export function useRelatedTaskLink(workspaceId: string) {
  const { workflows } = useWorkflows(workspaceId);
  const host = useContext(RelatedTaskHostContext);

  return (workflowId: string, taskId: string): RelatedTaskLinkProps | null =>
    host(workspaceId, resolveWorkflowKey(workflows, workflowId), taskId);
}

/**
 * @deprecated Scaffolding, removed in this feature's final phase.
 *
 * The surfaces being replaced (the related-tasks dialog, the relations table and
 * the old graphs) still navigate while they wait their turn to be deleted. This
 * keeps them working over the new link builder so no commit in between is
 * broken, and it already drops the `window.open` the old hook used: a related
 * task now opens in place even from a surface that is on its way out.
 */
export function useNavigateToRelatedTask(workspaceId: string) {
  const router = useRouter();
  const link = useRelatedTaskLink(workspaceId);

  return (workflowId: string, taskId: string): boolean => {
    const props = link(workflowId, taskId);
    if (!props) return false;
    void router.navigate(props);
    return true;
  };
}
